// Event-category model for the admin activity log filter. Single source of
// truth shared by the API (translates a category key into event_type LIKE
// filters) and the web dropdown (keys + labels), so the two can never drift.
//
// activity_log.event_type has two families (see apps/web/src/lib/activityFormat.ts):
//   - trigger events  `<table>.<op>`            e.g. 'submissions.update'
//   - bespoke events  dotted codes written by API routes and cron jobs
//                     e.g. 'submission_file.uploaded_by_admin' (companies.ts),
//                          'company.invite_reminder.7d' (scheduled.ts)
// Families with variable suffixes match by PREFIX (every prefix ends with '.'
// so it can only match whole segments); fixed codes match by exact eventType.

export const ACTIVITY_CATEGORY_KEYS = ['submissions', 'documents', 'vendor', 'system'] as const;

export type ActivityCategoryKey = (typeof ACTIVITY_CATEGORY_KEYS)[number];

export const ACTIVITY_CATEGORIES: Record<
  ActivityCategoryKey,
  { label: string; prefixes: readonly string[]; eventTypes: readonly string[] }
> = {
  submissions: { label: 'Reviews & submissions', prefixes: ['submissions.'], eventTypes: [] },
  documents: {
    label: 'Documents & policies',
    // Trigger events from the file/adoption tables, plus the bespoke event the
    // API writes when an admin uploads a document on the vendor's behalf
    // (apps/api/src/routes/companies.ts — actor is an admin, so it belongs
    // here, not under system).
    prefixes: ['submission_files.', 'submission_adoptions.'],
    eventTypes: ['submission_file.uploaded_by_admin'],
  },
  vendor: { label: 'Vendor record', prefixes: ['companies.'], eventTypes: [] },
  system: {
    // Cron/notification events written by apps/api/src/scheduled.ts. The
    // reminder families carry variable suffixes (e.g. '.7d') so they match by
    // prefix; the file-expiry events are exact codes.
    label: 'Notifications & system',
    prefixes: ['company.invite_reminder.', 'company.compliance_expiring.'],
    eventTypes: ['submission_file.expiry_reminder', 'submission_file.expired'],
  },
};

/**
 * Category a stored event_type belongs to, or null when uncategorized (e.g.
 * 'drivers.*', 'review_notifications.*', 'documents.*' — visible under
 * "All activity" only; deliberate v1 scope. Most other audited tables are
 * global-scope with no company_id, so they never reach the per-company view.)
 */
export function activityCategoryFor(eventType: string): ActivityCategoryKey | null {
  for (const key of ACTIVITY_CATEGORY_KEYS) {
    const { prefixes, eventTypes } = ACTIVITY_CATEGORIES[key];
    if (eventTypes.includes(eventType)) return key;
    if (prefixes.some((p) => eventType.startsWith(p))) return key;
  }
  return null;
}
